/**
 * «Скрины» переписок с клиентом во вкладке «Новый год» (29.10.2026). Это ПДн клиента и третьих лиц: файлы только через API
 * офису (routes/ny/shots.ts, в R2 зашифрованы), сюда — авторизованным fetch → objectURL (useShotUrl), без прямых ссылок.
 * С телефона — сразу несколько: ужатие по ширине ≤ 1280 и площади ≤ 16 Мп, всегда перекодируем (nyShrink.ts).
 * Тап по миниатюре — лайтбокс на весь экран (длинный скрин листается); удалить — кто загрузил или ЕО.
 */
import { useEffect, useState } from "react";
import { fmtStamp } from "./nyModel";
import { errText, useNyShots, useShotUrl, type NyShot } from "./useNy";
import { NyDel } from "./NyNotes";
import ny from "./dcNy.module.css";

function Thumb({ s, onOpen }: { s: NyShot; onOpen: (s: NyShot, url: string) => void }) {
  const url = useShotUrl(s.id);
  return (
    <button type="button" className={ny.th} title={`${s.uploaded_by_name || "—"} · ${fmtStamp(s.created_at)}`}
      onClick={() => { if (url) onOpen(s, url); }} disabled={!url}>
      {url ? <img src={url} alt="Скрин переписки" /> : url === "" ? "не открылся" : "…"}
    </button>
  );
}

/** Лайтбокс (как MediaViewer отчёта): тап по фону / Esc / ✕ — закрыть; картинка во всю ширину, длинная — листается. */
function Lightbox({ s, url, onClose, onDelete }: { s: NyShot; url: string; onClose: () => void; onDelete: (() => Promise<void>) | null }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className={ny.lb} role="dialog" aria-modal="true" aria-label="Скрин переписки" onClick={onClose}>
      <div className={ny.lbBar} onClick={(e) => e.stopPropagation()}>
        <span>{s.uploaded_by_name || "—"} · {fmtStamp(s.created_at)}{s.w && s.h ? ` · ${s.w}×${s.h}` : ""}</span>
        {onDelete ? <NyDel onDelete={onDelete} label="Удалить скрин" idle="Удалить" /> : null}
        <button type="button" className={ny.lbBtn} onClick={onClose} aria-label="Закрыть">✕</button>
      </div>
      <div className={ny.lbScroll}><img src={url} alt="Скрин переписки" onClick={(e) => e.stopPropagation()} /></div>
    </div>
  );
}

export function NyShots({ cardRef, onChanged, onToast }: { cardRef: number; onChanged: () => void; onToast: (m: string) => void }) {
  const { shots, error, busy, pending, note, upload, retry, remove } = useNyShots(cardRef, onChanged);
  const [view, setView] = useState<{ s: NyShot; url: string } | null>(null);
  const del = async (id: number) => {
    try { await remove(id); setView(null); onToast("Скрин удалён"); } catch (e) { onToast(errText(e, "Не удалось удалить скрин")); }
  };

  return (
    <div className={ny.panel}>
      <div className={ny.shots}>
        <label className={`${ny.th} ${ny.thAdd}`} title="Добавить скрины (можно несколько)" aria-label="Добавить скрины">+
          <input type="file" accept="image/*" multiple disabled={!!busy}
            onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ""; void upload(files); }} />
        </label>
        {(shots ?? []).map((s) => <Thumb key={s.id} s={s} onOpen={(x, url) => setView({ s: x, url })} />)}
      </div>
      {busy ? (
        <div className={ny.busy}>{busy.stage === "shrink" ? `Готовлю снимки… ${busy.done + 1} из ${busy.total}` : `Отправляю… ${busy.done} из ${busy.total}`}</div>
      ) : null}
      {note ? <div className={ny.err} role="alert">{note}</div> : null}
      {pending && !busy ? (
        <div className={ny.busy}>Ждут отправки: {pending}<button type="button" className={ny.retry} onClick={() => { void retry(); }}>Повторить</button></div>
      ) : null}
      {error && !shots ? <div className={ny.err}>Ошибка загрузки: {error}</div>
        : !shots ? <div className={ny.muted}>Загрузка…</div>
        : shots.length === 0 && !busy ? <div className={ny.muted}>Скринов пока нет — «+» добавит с телефона, можно несколько сразу.</div> : null}
      {view ? <Lightbox s={view.s} url={view.url} onClose={() => setView(null)} onDelete={view.s.can_delete ? () => del(view.s.id) : null} /> : null}
    </div>
  );
}
